/**
 * Decoding raw safetensors payload bytes into floats.
 *
 * Two things make this fiddly enough to deserve its own module:
 *
 *  1. **Endianness.** The safetensors format stores every scalar little-endian,
 *     unconditionally — it is not a host-order format. The prototype this was
 *     ported from did `new Uint16Array(buffer)`, which reads in *host* order and
 *     therefore silently produces byte-swapped garbage on a big-endian machine.
 *     Every read here goes through `DataView` with an explicit `littleEndian`
 *     flag instead. That also sidesteps alignment: a Range response sliced at an
 *     arbitrary byte offset is not guaranteed to be 4- or 8-byte aligned, and
 *     `new Float32Array(buffer, offset)` throws outright when it isn't.
 *
 *  2. **bfloat16 and float16 are not the same 16 bits.** bf16 is the top half of
 *     an f32 (8 exponent bits, 7 mantissa bits) so it decodes by a shift; f16 is
 *     IEEE binary16 (5 exponent bits, 10 mantissa bits) and needs a real decode
 *     including subnormals, infinities and NaN. Confusing them yields numbers
 *     that are wrong by many orders of magnitude but still look plausible, which
 *     is the worst kind of wrong for a diagnostic instrument.
 *
 * Everything here is pure: bytes in, `Float32Array` out, no I/O.
 */

/**
 * Anything a tensor can be handed to the analysis functions as.
 *
 * Typed arrays are the normal case. The union is written so `for (const w of …)`
 * yields `number` rather than `number | undefined` — with
 * `noUncheckedIndexedAccess` on, iteration is both faster and quieter than
 * indexing in the hot loops.
 */
export type NumericArray = Float32Array | Float64Array | readonly number[];

/** The dtypes this module can turn into floats. */
export type FloatDtype = "BF16" | "F16" | "F32" | "F64";

const FLOAT_DTYPE_BYTES: Readonly<Record<FloatDtype, number>> = {
  BF16: 2,
  F16: 2,
  F32: 4,
  F64: 8,
};

export function isFloatDtype(dtype: string): dtype is FloatDtype {
  return Object.hasOwn(FLOAT_DTYPE_BYTES, dtype);
}

/** Element width in bytes, or `undefined` for a dtype we cannot decode. */
export function floatDtypeByteWidth(dtype: string): number | undefined {
  return isFloatDtype(dtype) ? FLOAT_DTYPE_BYTES[dtype] : undefined;
}

/**
 * Decode one IEEE binary16 (half) bit pattern.
 *
 * Split out and written the long way because the three special exponent cases
 * are where naive implementations go wrong: exponent 0 is *not* an implicit-one
 * mantissa (subnormals, and the value `2^-24 × frac` covers ±0 for free), and
 * exponent 0x1F is inf/NaN rather than a huge finite number.
 *
 * `sign * 0` yields `-0` for a negative zero input, which is deliberate:
 * sparsity counting treats `-0` as zero, but a decoder that quietly normalized
 * it away would be lying about the bits on disk.
 */
/** Scratch view for the float encoders below; module-private and reused. */
const ENCODE_VIEW = new DataView(new ArrayBuffer(4));

export function decodeFloat16Bits(bits: number): number {
  const sign = bits & 0x8000 ? -1 : 1;
  const exponent = (bits >> 10) & 0x1f;
  const fraction = bits & 0x03ff;

  if (exponent === 0) {
    // Subnormal (and zero): no implicit leading 1, fixed exponent of -14, and
    // the 10-bit fraction scaled by 2^-10 — i.e. 2^-24 per fraction step.
    return sign * fraction * 2 ** -24;
  }
  if (exponent === 0x1f) {
    return fraction === 0 ? sign * Number.POSITIVE_INFINITY : Number.NaN;
  }
  return sign * (1 + fraction / 1024) * 2 ** (exponent - 15);
}

/**
 * Decode `bytes` as a packed array of `dtype`, little-endian, into f32.
 *
 * F64 inputs are narrowed to f32, which loses mantissa bits. That is acceptable
 * here — every statistic downstream accumulates in f64 anyway and no real
 * checkpoint stores weights as f64 — but it is a lossy step, so it is stated
 * rather than hidden.
 *
 * Throws (naming the dtype) rather than returning an empty array for anything
 * not decodable to float: an int or packed 4-bit tensor analysed as if it were
 * float would produce a confident, completely meaningless distribution.
 */
export function decodeFloats(
  bytes: ArrayBufferLike | ArrayBufferView,
  dtype: string,
): Float32Array {
  const width = floatDtypeByteWidth(dtype);
  if (width === undefined) {
    throw new Error(
      `Cannot decode dtype ${dtype} to float — only BF16, F16, F32 and F64 are float-decodable (integer, boolean, float8 and packed sub-byte dtypes need a dequantization scheme, not a cast)`,
    );
  }

  // `ArrayBuffer.isView`, not `instanceof ArrayBuffer`: the latter is false for a
  // SharedArrayBuffer, which would then fall through to the view branch and fail
  // on a `.buffer` that isn't there.
  const view = ArrayBuffer.isView(bytes)
    ? new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
    : new DataView(bytes);

  if (view.byteLength % width !== 0) {
    throw new Error(
      `Cannot decode ${view.byteLength} bytes as ${dtype}: not a whole number of ${width}-byte elements — the byte range is truncated or misaligned`,
    );
  }

  const count = view.byteLength / width;
  const out = new Float32Array(count);

  if (dtype === "BF16") {
    // bf16 *is* the upper 16 bits of an f32, so the decode is a shift into the
    // high half of the word. Aliasing a Uint32Array over the output's own buffer
    // is safe on any host: both views use host order, so they agree with each
    // other whatever that order is. Only the *file* read needs to be forced
    // little-endian, and `getUint16(…, true)` does that.
    const bits = new Uint32Array(out.buffer);
    for (let i = 0; i < count; i += 1) {
      bits[i] = view.getUint16(i * 2, true) << 16;
    }
    return out;
  }

  if (dtype === "F16") {
    for (let i = 0; i < count; i += 1) {
      out[i] = decodeFloat16Bits(view.getUint16(i * 2, true));
    }
    return out;
  }

  if (dtype === "F32") {
    for (let i = 0; i < count; i += 1) {
      out[i] = view.getFloat32(i * 4, true);
    }
    return out;
  }

  for (let i = 0; i < count; i += 1) {
    out[i] = view.getFloat64(i * 8, true);
  }
  return out;
}

/**
 * Round an f32 to the nearest IEEE-754 binary16, returning its bit pattern.
 *
 * The encode counterpart of `decodeFloat16Bits`, and needed for a reason that
 * only became visible against real `mx` output: **dequantization returns the
 * dtype of the scale planes, not f32.** A model whose scales are f16 has f16
 * weights at runtime, so computing the dequantized values in f32 and stopping
 * there reports a distribution finer than the one the model actually has —
 * smooth where reality has f16 steps.
 *
 * Round-to-nearest-even throughout, matching IEEE-754 and the hardware. The
 * carry in the normal path is deliberately left to ripple out of the mantissa
 * into the exponent field: adding one to `(exp << 10) | mant` handles
 * mantissa overflow into the next binade without a special case, and overflows
 * to the Inf encoding on its own when the exponent saturates.
 */
export function encodeFloat16Bits(value: number): number {
  ENCODE_VIEW.setFloat32(0, value, true);
  const x = ENCODE_VIEW.getUint32(0, true);
  const sign = (x >>> 16) & 0x8000;
  const exp = (x >>> 23) & 0xff;
  let mant = x & 0x7fffff;

  // Inf and NaN. A NaN must stay a NaN: preserving one payload bit prevents a
  // signalling NaN collapsing to Inf, which would silently turn "this tensor is
  // corrupt" into "this tensor has huge values".
  if (exp === 0xff) return sign | 0x7c00 | (mant === 0 ? 0 : 0x200);

  const e = exp - 127 + 15;
  if (e >= 0x1f) return sign | 0x7c00; // overflows the f16 range
  if (e > 0) {
    let half = (e << 10) | (mant >>> 13);
    const rest = mant & 0x1fff;
    if (rest > 0x1000 || (rest === 0x1000 && (half & 1) === 1)) half += 1;
    return sign | half;
  }

  // Subnormal, or too small to represent at all.
  if (e < -10) return sign;
  mant |= 0x800000; // restore the implicit leading bit before shifting
  const shift = 14 - e;
  let half = mant >>> shift;
  const rest = mant & ((1 << shift) - 1);
  const tie = 1 << (shift - 1);
  if (rest > tie || (rest === tie && (half & 1) === 1)) half += 1;
  return sign | half;
}

/** Round an f32 through binary16 and back — the value MLX would actually hold. */
export function roundToFloat16(value: number): number {
  return decodeFloat16Bits(encodeFloat16Bits(value));
}

/**
 * Round an f32 to bfloat16 precision.
 *
 * bfloat16 is the top 16 bits of an f32, so this is a truncation with
 * round-to-nearest-even on the discarded low half — no exponent rework needed,
 * unlike binary16.
 */
export function roundToBFloat16(value: number): number {
  ENCODE_VIEW.setFloat32(0, value, true);
  const x = ENCODE_VIEW.getUint32(0, true);
  if (((x >>> 23) & 0xff) === 0xff) return value; // Inf/NaN pass through
  const lsb = (x >>> 16) & 1;
  const rounded = (x + 0x7fff + lsb) & 0xffff0000;
  ENCODE_VIEW.setUint32(0, rounded, true);
  return ENCODE_VIEW.getFloat32(0, true);
}

/**
 * Round a value to the precision a storage dtype can actually hold.
 *
 * Unknown dtypes pass through unchanged rather than throwing: this is a
 * fidelity refinement, and refusing to return a number because we don't
 * recognise a plane dtype would be a worse failure than returning a slightly
 * too-precise one.
 */
export function roundToDtype(value: number, dtype: string): number {
  if (dtype === "F16") return roundToFloat16(value);
  if (dtype === "BF16") return roundToBFloat16(value);
  return value;
}
